/** The AWS deployment guide, read inside the admin page. Same text as the PDF. */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, Loader2 } from 'lucide-react';

export function AwsGuideDialog({ open, onOpenChange }) {
  const [guide, setGuide] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || guide) return;
    api.get('/aws-deploy/guide')
      .then(({ data }) => setGuide(data))
      .catch(e => setError(e?.response?.data?.detail || 'Could not load the guide'));
  }, [open, guide]);

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/aws-deploy/guide.pdf`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto"
                     data-testid="aws-guide-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">
            {guide?.title || 'Deploying Panorix on AWS'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between -mt-2 mb-2">
          <p className="text-[12px] text-muted-foreground">
            {guide?.subtitle || 'A step-by-step guide for a first-time deployer'}
          </p>
          <Button size="sm" variant="outline" asChild data-testid="aws-guide-pdf-btn">
            <a href={pdfUrl} target="_blank" rel="noreferrer">
              <Download className="w-3.5 h-3.5 mr-1.5" />Download PDF
            </a>
          </Button>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}
        {!guide && !error && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground py-8">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />Loading the guide…
          </div>
        )}

        {guide?.sections?.map(section => (
          <section key={section.heading} className="space-y-2 pb-4">
            <h3 className="text-[13px] font-semibold text-orange-700">{section.heading}</h3>
            {(section.intro || []).map((p, i) => (
              <p key={i} className="text-[12px] text-muted-foreground leading-relaxed">{p}</p>
            ))}
            {(section.images || []).map(img => (
              <figure key={img.url} className="my-2">
                <img src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                     alt={img.caption}
                     className="w-full rounded-md border border-slate-200"
                     data-testid="aws-guide-image" />
                <figcaption className="text-[11px] text-muted-foreground mt-1 italic">
                  {img.caption}
                </figcaption>
              </figure>
            ))}
            {(section.steps || []).length > 0 && (
              <ol className="space-y-2 list-decimal pl-4">
                {section.steps.map(s => (
                  <li key={s.title} className="text-[12px]">
                    <span className="font-medium text-foreground">{s.title}</span>
                    <p className="text-[12px] text-muted-foreground leading-relaxed">{s.detail}</p>
                  </li>
                ))}
              </ol>
            )}
            {section.table && (
              <table className="w-full text-[11px] border border-slate-200">
                <thead>
                  <tr className="bg-slate-900 text-white text-left">
                    {section.table.headers.map(h => (
                      <th key={h} className="px-2 py-1.5 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.table.rows.map((row, i) => (
                    <tr key={i} className={i % 2 ? 'bg-slate-50' : ''}>
                      {row.map((c, j) => (
                        <td key={j} className="px-2 py-1.5 align-top text-muted-foreground border-t border-slate-200">{c}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export default AwsGuideDialog;
